import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../shared/ui/feedback/ToastContext';
import { useRealtime, useRealtimeStatus } from '../../shared/realtime/RealtimeProvider';
import { useI18n } from '../../i18n/I18nProvider';
import {
  dismissNotification,
  dismissReadNotifications,
  fetchNotificationCounts,
  fetchNotificationPage,
  markAllNotificationsRead,
  markNotificationRead,
  restoreNotifications,
  runNotificationAction,
  type NotificationItem,
  type NotificationTabKey,
} from './api';

/** Only while the socket is down; with it up, the server tells us. */
const FALLBACK_POLL_MS = 120_000;

export interface Counts {
  unread: number;
  needs_you: number;
}

interface Options {
  /** Which list to load. */
  tab: NotificationTabKey;
  /** False keeps only the counts live (the closed bell); true loads and follows the list. */
  active: boolean;
  /** Called for every notification pushed while this is mounted (the live pop-up). */
  onArrived?: (item: NotificationItem) => void;
}

function fitsTab(item: NotificationItem, tab: NotificationTabKey) {
  if (tab === 'unread') return !item.is_read;
  if (tab === 'needs_you') return item.needs_you;
  return true;
}

/**
 * The state behind the bell and the Notifications page: one page of a tab, its cursor, the
 * two counts, and every change -- each applied at once and rolled back, with a message, when
 * the server refuses. It used to swallow every failure (`.catch(() => {})`), so the badge
 * dropped, then jumped back on the next poll: the "13" that never went away.
 */
export function useNotifications({ tab, active, onArrived }: Options) {
  const { t } = useI18n();
  const toast = useToast();
  const connected = useRealtimeStatus();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState<Counts>({ unread: 0, needs_you: 0 });
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const onArrivedRef = useRef(onArrived);
  onArrivedRef.current = onArrived;

  const applyCounts = (data: any) => {
    if (!data) return;
    setCounts((prev) => ({
      unread: typeof data.unread === 'number' ? data.unread : prev.unread,
      needs_you: typeof data.needs_you === 'number' ? data.needs_you : prev.needs_you,
    }));
  };

  const refreshCounts = useCallback(() => {
    fetchNotificationCounts().then(applyCounts).catch(() => {
      // A missed refresh is corrected by the next push, focus or poll; nothing to tell anyone.
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetchNotificationPage(tabRef.current)
      .then((page) => {
        setItems(page.items || []);
        setNext(page.next || null);
        applyCounts(page);
      })
      .catch(() => toast.error(t('notifications.loadFailed')))
      .finally(() => setLoading(false));
  }, [t, toast]);

  const loadMore = useCallback(() => {
    if (!next || loadingMore) return;
    setLoadingMore(true);
    fetchNotificationPage(tabRef.current, next)
      .then((page) => {
        setItems((prev) => {
          const seen = new Set(prev.map((n) => n.id));
          return [...prev, ...(page.items || []).filter((n) => !seen.has(n.id))];
        });
        setNext(page.next || null);
        applyCounts(page);
      })
      .catch(() => toast.error(t('notifications.loadFailed')))
      .finally(() => setLoadingMore(false));
  }, [next, loadingMore, t, toast]);

  // Counts on mount, and whenever the tab comes back into view: a laptop that slept for an
  // hour should not show the count it had when it closed.
  useEffect(() => {
    refreshCounts();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshCounts(); };
    window.addEventListener('focus', refreshCounts);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshCounts);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshCounts]);

  // The list: loaded when opened and on every tab change.
  useEffect(() => {
    if (active) load();
  }, [active, tab, load]);

  // Slow polling only while the live connection is down.
  useEffect(() => {
    if (connected) return undefined;
    const id = setInterval(refreshCounts, FALLBACK_POLL_MS);
    return () => clearInterval(id);
  }, [connected, refreshCounts]);

  useRealtime('/user/queue/notifications', (push) => {
    applyCounts(push);
    if (push?.kind === 'created' && push.notification) {
      const item: NotificationItem = push.notification;
      if (active && fitsTab(item, tabRef.current)) {
        setItems((prev) => (prev.some((n) => n.id === item.id) ? prev : [item, ...prev]));
      }
      onArrivedRef.current?.(item);
    }
  });

  const patch = (id: string, change: Partial<NotificationItem>) =>
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, ...change } : n)));

  const markRead = useCallback(async (item: NotificationItem) => {
    if (item.is_read) return;
    patch(item.id, { is_read: 1 });
    setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1) }));
    try {
      await markNotificationRead(item.id);
    } catch {
      patch(item.id, { is_read: 0 });
      refreshCounts();
      toast.error(t('notifications.actionFailed'));
    }
  }, [refreshCounts, t, toast]);

  const markAllRead = useCallback(async () => {
    const before = items;
    const beforeCounts = counts;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    setCounts((c) => ({ ...c, unread: 0 }));
    try {
      await markAllNotificationsRead();
    } catch {
      setItems(before);
      setCounts(beforeCounts);
      toast.error(t('notifications.actionFailed'));
    }
  }, [items, counts, t, toast]);

  const restore = useCallback(async (ids: string[]) => {
    try {
      await restoreNotifications(ids);
      load();
    } catch {
      toast.error(t('notifications.actionFailed'));
    }
  }, [load, t, toast]);

  const dismiss = useCallback(async (item: NotificationItem) => {
    const before = items;
    setItems((prev) => prev.filter((n) => n.id !== item.id));
    if (!item.is_read) setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1) }));
    try {
      await dismissNotification(item.id);
      toast.show({
        message: t('notifications.dismissed'),
        action: { label: t('notifications.undo'), onClick: () => restore([item.id]) },
      });
    } catch {
      setItems(before);
      refreshCounts();
      toast.error(t('notifications.actionFailed'));
    }
  }, [items, refreshCounts, restore, t, toast]);

  const clearRead = useCallback(async () => {
    const before = items;
    setItems((prev) => prev.filter((n) => !n.is_read));
    try {
      const { ids } = await dismissReadNotifications();
      if (ids?.length) {
        toast.show({
          message: t('notifications.cleared', { count: ids.length }),
          action: { label: t('notifications.undo'), onClick: () => restore(ids) },
        });
      }
    } catch {
      setItems(before);
      toast.error(t('notifications.actionFailed'));
    }
  }, [items, restore, t, toast]);

  /** Runs an "execute" action; the row comes back with its outcome and no buttons. */
  const runAction = useCallback(async (item: NotificationItem, actionKey: string) => {
    try {
      const updated = await runNotificationAction(item.id, actionKey);
      if (tabRef.current === 'needs_you') {
        setItems((prev) => prev.filter((n) => n.id !== item.id));
      } else {
        patch(item.id, updated);
      }
      refreshCounts();
      return true;
    } catch (e: any) {
      toast.error(e?.message || t('notifications.actionFailed'));
      // Whatever made it fail (someone else approved first, the event filled) is now the truth.
      load();
      return false;
    }
  }, [load, refreshCounts, t, toast]);

  return {
    items, next, loading, loadingMore, counts,
    load, loadMore, refreshCounts, markRead, markAllRead, dismiss, clearRead, runAction,
    hasRead: items.some((n) => n.is_read),
  };
}
